<template>
	<div
		:id="underwriterId"
		v-if="!underwriter.disabled"
		v-bind:class="[
			'panel',
			'quote-panel',
			'shadow',
			{
				'panel-default': !selected,
				'panel-primary': selected,
				'quote-calculating': calculating,
				'quote-disabled': underwriter.disabled,
			}
		]"
	>
		<p class="text-center m-b-0-i quote-header">
			<slot name="header"></slot>
		</p>
		<div
			class="quote-disabled-title"
			v-if="underwriter.disabled"
			v-text="'Quote Unavailable'"
		/>
		<h2
			class="text-center quote-title"
			v-else
			v-text="'$'+costOfInsurance.toFixed(2)"
		/>
		<div class="text-center quote-description" v-if="underwriter.description">
			{{underwriter.description}}
		</div>
		<!--
		<div class="text-center quote-error" v-if="Array.isArray(underwriter.error) && underwriter.error.length">
			<div
				v-for="(errorMessage, index) in underwriter.error" :key="index"
				v-text="errorMessage"
			/>
		</div>
		-->
		<div class="text-center quote-form">
			<slot name="form"></slot>
		</div>
		<div class="text-center quote-footer">
			<button
				v-if="selectShow"
				type="button"
				v-bind:class="[
					'btn',
					'btn-wd',
					'p-x-45-i',
					{
						'btn-blue active': selected,
					}
				]"
				@click="$emit('select')"
				:disabled="selectDisabled || underwriter.disabled"
				v-text="selected ? 'Selected' : 'Select'"
			/>
			<div
				v-if="endorsements && endorsements.length"
				class="quote-info"
			>
				<v-popover
					popoverWrapperClass="popover-wrapper"
					popoverBaseClass="tooltip tooltip-popover"
					:container="'#' + underwriterId"
					v-on:hide="onHideTooltip()"
				>
					<span
						class="ti-info-alt"
						v-tooltip="{
							placement: 'bottom',
							html: true
						}"
					/>
					<template slot="popover">
						<div class="text-left">
							<h3 class="m-t-0-i">Endorsements</h3>
							<ul v-if="endorsements">
								<li
									v-for="endorsement in endorsements" :key="endorsement.code"
									v-bind:class="{
										'endorsement': true,
										'endorsement-selected': currentEndorsement === endorsement.code
									}"
									@click="setCurrentEndorsement(endorsement.code)"
								>
									<div class="endorsement-title">{{ endorsement.description }}</div>
									<div
										class="endorsement-description"
										v-if="endorsement.details"
										v-html="endorsement.details"
									/>
								</li>
							</ul>
							<p class="text-center p-b-20" v-else>No endorsements to display.</p>
							<div v-if="underwriter.product_liability_policy">
								<h3 class="quote-legal-header">Legal</h3>
								<i class="quote-legal-descr">(links will open in new tab)</i>
								<ul>
									<li v-if="underwriter.product_liability_policy">
										<a
											target="_blank"
											rel="noopener noreferrer"
											:href="underwriter.product_liability_policy"
										>
											Product Disclosure Statement
										</a>
									</li>
								</ul>
							</div>
						</div>
					</template>
				</v-popover>
			</div>
		</div>
	</div>
</template>

<script>
	import { isEmpty } from 'lodash'

	export default {
		data: function () {
			return {
				currentEndorsement: null,
				id: Math.random().toString(36).substr(2, 10),
			}
		},

		props: {
			selectDisabled: {
				type: Boolean
			},
			selectShow: {
				type: Boolean,
				default: true,
			},
			selected: {
				type: Boolean
			},
			calculating: {
				type: Boolean
			},
			underwriter: {
				type: Object
			}
		},
		computed: {
			underwriterId () {
				return `quote_${this.id}`
			},

			endorsements () {
				return this.underwriter.summary && this.underwriter.summary.endorsements || []
			},

			costOfInsurance () {
				if (isEmpty(this.underwriter.summary)) {
					return 0
				}

				return this.underwriter.summary.totalPrice
			}
		},

		methods: {
			setCurrentEndorsement (code) {
				this.currentEndorsement = this.currentEndorsement === code ? null : code;
			},
			onHideTooltip () {
				this.currentEndorsement = null;
			},
		},
	}
</script>

<style lang="scss">
	.quote-panel {
		position: relative;
		margin-left: 0px;
		margin-right: 0px;
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		flex-shrink: 0;
		flex-basis: auto;
		max-width: initial;
		min-width: 230px;

		.tooltip {
			z-index: 1;
		}

		@media screen and (min-width: 425px) {
			padding: 0 25px;
		}

		@media screen and (min-width: 640px) {
			flex-basis: 30%;
			max-width: 50%;
			max-width: calc(50% - 20px);
			margin-left: 10px;
			margin-right: 10px;
		}
	}

	.quote-header {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 72px;
		margin-top: 20px;
	}

	.quote-calculating:before {
		content: "";
		display: block;
		position: absolute;
		z-index: 1;
		top: 0px;
		left: 0px;
		right: 0px;
		bottom: 0px;
		background-color: rgba(0,0,0,0.3);
	}

	.quote-calculating:after {
		content: "";
		display: block;
		position: absolute;
		top: 50%;
		left: 50%;
		font-size: 10px;
		width: 1em;
		height: 1em;
		margin-top: -0.5em;
		animation: spinner 1500ms infinite linear;
		border-radius: 0.5em;
		box-shadow: rgba(0, 0, 0, 0.75) 1.5em 0 0 0, rgba(0, 0, 0, 0.75) 1.1em 1.1em 0 0, rgba(0, 0, 0, 0.75) 0 1.5em 0 0, rgba(0, 0, 0, 0.75) -1.1em 1.1em 0 0, rgba(0, 0, 0, 0.75) -1.5em 0 0 0, rgba(0, 0, 0, 0.75) -1.1em -1.1em 0 0, rgba(0, 0, 0, 0.75) 0 -1.5em 0 0, rgba(0, 0, 0, 0.75) 1.1em -1.1em 0 0;
	}

	.quote-disabled {
		opacity: 0.9;
		color: #9A9A9A;
	}

	.quote-title {
		margin: 15px 0;

		@media screen and (min-width: 1025px) {
			margin: 30px 0;
		}
	}

	.quote-disabled-title {
		line-height: 40px;
    	margin: 15px 0 0 0;
		text-align: center;

		@media screen and (min-width: 1025px) {
			margin: 30px 0 0 0;
		}
	}

	.quote-form,
	.quote-description,
	.quote-error {
		flex-grow: 1;
    	display: flex;
    	flex-direction: column;
    	justify-content: flex-end;
		align-items: center;
	}

	.quote-form {
		margin: 10px 0;

		& label:not(:first-of-type) {
			margin-top: 10px;
		}

		& .form-control {
			width: 80%;
		}
	}

	.quote-footer {
		margin: 20px 0 30px 0;
	}

	.quote-info {
		color: #9A9A9A;
		position: absolute;
		right: 8px;
		bottom: 5px;
		cursor: pointer;
	}

	.endorsement {
		cursor: pointer;

		&:hover {
			.endorsement-title {
				text-decoration: underline;
			}
		}
	}

	.endorsement-description {
		opacity: 0.6;
		height: 0px;
		overflow-x: hidden;
		overflow-y: auto;
		max-height: 300px;
		transform: scaleY(0);
		transform-origin: top;
		transition: transform 0.26s;

		.endorsement-selected & {
			height: auto;
			transform: scaleY(1);
		}
	}

	.quote-legal-header {
		display: inline-block;
	}

	.quote-legal-descr {
		font-weight: 300;
	}
</style>
